import {
  LeitoOperacional,
  LeitoPayload,
  PerfilAssistencial,
  PontoGeracaoLeito,
  PontoGeracaoPayload,
} from '../../types/leito';

export interface LeitoFormState {
  clienteEmpresaId: string;
  codigoLeito: string;
  identificadorInternoCliente: string;
  municipio: string;
  uf: string;
  regiao: string;
  perfilAssistencial: PerfilAssistencial;
  geraResiduoGrupoA: boolean;
  geraResiduoGrupoBMedicamento: boolean;
  geraResiduoGrupoEPerfurocortante: boolean;
  geraResiduoAntineoplasico: boolean;
  requerCadeiaEspecializada: boolean;
  cep: string;
  latitudeEsperada: string;
  longitudeEsperada: string;
  origemGeolocalizacao: 'manual' | 'navegador' | 'geocodificada';
  precisaoMetros: string;
  geolocalizacaoAtualizadaEm: string;
  politicaGps: 'obrigatorio' | 'opcional' | 'desabilitado';
  toleranciaNormalMetros: string;
  toleranciaAlertaMetros: string;
}

export function emptyLeitoForm(clienteEmpresaId = ''): LeitoFormState {
  return {
    clienteEmpresaId,
    codigoLeito: '',
    identificadorInternoCliente: '',
    municipio: '',
    uf: '',
    regiao: '',
    perfilAssistencial: 'curativo_complexo',
    geraResiduoGrupoA: true,
    geraResiduoGrupoBMedicamento: false,
    geraResiduoGrupoEPerfurocortante: true,
    geraResiduoAntineoplasico: false,
    requerCadeiaEspecializada: false,
    cep: '',
    latitudeEsperada: '',
    longitudeEsperada: '',
    origemGeolocalizacao: 'manual',
    precisaoMetros: '',
    geolocalizacaoAtualizadaEm: '',
    politicaGps: 'opcional',
    toleranciaNormalMetros: '100',
    toleranciaAlertaMetros: '500',
  };
}

export function leitoToForm(leito: LeitoOperacional, ponto: PontoGeracaoLeito | null): LeitoFormState {
  return {
    clienteEmpresaId: leito.clienteEmpresaId,
    codigoLeito: leito.codigoLeito,
    identificadorInternoCliente: leito.identificadorInternoCliente ?? '',
    municipio: leito.municipio,
    uf: leito.uf,
    regiao: leito.regiao ?? '',
    perfilAssistencial: leito.perfilAssistencial ?? 'outro',
    geraResiduoGrupoA: leito.geraResiduoGrupoA,
    geraResiduoGrupoBMedicamento: leito.geraResiduoGrupoBMedicamento,
    geraResiduoGrupoEPerfurocortante: leito.geraResiduoGrupoEPerfurocortante,
    geraResiduoAntineoplasico: leito.geraResiduoAntineoplasico,
    requerCadeiaEspecializada: leito.requerCadeiaEspecializada,
    cep: ponto?.cep ? formatCep(ponto.cep) : '',
    latitudeEsperada: ponto?.latitudeEsperada?.toString() ?? '',
    longitudeEsperada: ponto?.longitudeEsperada?.toString() ?? '',
    origemGeolocalizacao: ponto?.origemGeolocalizacao ?? 'manual',
    precisaoMetros: ponto?.precisaoMetros?.toString() ?? '',
    geolocalizacaoAtualizadaEm: ponto?.geolocalizacaoAtualizadaEm ?? '',
    politicaGps: ponto?.politicaGps ?? 'opcional',
    toleranciaNormalMetros: String(ponto?.toleranciaNormalMetros ?? 100),
    toleranciaAlertaMetros: String(ponto?.toleranciaAlertaMetros ?? 500),
  };
}

export function formatCep(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/^(\d{5})(\d)/, '$1-$2');
}

function optional(value: string): string | undefined {
  return value.trim() || undefined;
}

export function leitoPayload(form: LeitoFormState): LeitoPayload {
  return {
    clienteEmpresaId: form.clienteEmpresaId,
    codigoLeito: form.codigoLeito.trim(),
    identificadorInternoCliente: optional(form.identificadorInternoCliente),
    municipio: form.municipio.trim(),
    uf: form.uf.trim().toUpperCase(),
    regiao: optional(form.regiao),
    perfilAssistencial: form.perfilAssistencial,
    geraResiduoGrupoA: form.geraResiduoGrupoA,
    geraResiduoGrupoBMedicamento: form.geraResiduoGrupoBMedicamento,
    geraResiduoGrupoEPerfurocortante: form.geraResiduoGrupoEPerfurocortante,
    geraResiduoAntineoplasico: form.geraResiduoAntineoplasico,
    requerCadeiaEspecializada: form.requerCadeiaEspecializada,
  };
}

export function hasPontoData(form: LeitoFormState): boolean {
  return Boolean(
    form.cep.replace(/\D/g, '') ||
    form.latitudeEsperada.trim() ||
    form.longitudeEsperada.trim()
  );
}

export function pontoPayload(form: LeitoFormState): PontoGeracaoPayload {
  const hasCoordinates = Boolean(form.latitudeEsperada.trim() && form.longitudeEsperada.trim());
  return {
    cep: form.cep ? form.cep.replace(/\D/g, '') : null,
    latitudeEsperada: hasCoordinates ? Number(form.latitudeEsperada) : null,
    longitudeEsperada: hasCoordinates ? Number(form.longitudeEsperada) : null,
    origemGeolocalizacao: hasCoordinates ? form.origemGeolocalizacao : null,
    precisaoMetros: hasCoordinates && form.precisaoMetros ? Number(form.precisaoMetros) : null,
    geolocalizacaoAtualizadaEm: hasCoordinates
      ? form.geolocalizacaoAtualizadaEm || new Date().toISOString()
      : null,
    politicaGps: form.politicaGps,
    toleranciaNormalMetros: Number(form.toleranciaNormalMetros),
    toleranciaAlertaMetros: Number(form.toleranciaAlertaMetros),
  };
}

export function validateLeitoForm(form: LeitoFormState, validateLocation: boolean): string[] {
  const errors: string[] = [];
  if (!form.clienteEmpresaId) errors.push('Selecione a empresa cliente.');
  if (!form.codigoLeito.trim()) errors.push('Informe o código do leito.');
  if (form.municipio.trim().length < 2) errors.push('Informe o município.');
  if (form.uf.trim().length !== 2) errors.push('Informe uma UF válida.');
  if (!validateLocation) return errors;

  const cep = form.cep.replace(/\D/g, '');
  if (cep && cep.length !== 8) errors.push('CEP deve conter oito dígitos.');
  const hasLatitude = Boolean(form.latitudeEsperada.trim());
  const hasLongitude = Boolean(form.longitudeEsperada.trim());
  if (hasLatitude !== hasLongitude) errors.push('Latitude e longitude devem ser informadas em conjunto.');
  if (hasLatitude) {
    const latitude = Number(form.latitudeEsperada);
    const longitude = Number(form.longitudeEsperada);
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) errors.push('Latitude deve estar entre -90 e 90.');
    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) errors.push('Longitude deve estar entre -180 e 180.');
  }
  const normal = Number(form.toleranciaNormalMetros);
  const alert = Number(form.toleranciaAlertaMetros);
  if (!Number.isInteger(normal) || normal < 0) errors.push('Tolerância normal inválida.');
  if (!Number.isInteger(alert) || alert < normal) errors.push('Tolerância de alerta deve ser maior ou igual à normal.');
  return errors;
}

